import { z } from 'zod';

import { RTESchema } from '../validators';

export const SearchAgentMessageSchema = z.object({
    id: z.string(),
    role: z.enum(['system', 'user', 'assistant', 'developer', 'update']),
    content: z.string(),
});

export const SearchAgentUserMessage = SearchAgentMessageSchema.extend({
    role: z.literal('user'),
});

export const SearchAgentAssistantMessage = SearchAgentMessageSchema.extend({
    role: z.literal('assistant'),
});

export const SearchAgentBaseMessageSchema = z.object({
    id: z.string(),
    message: z.string(),
    time: z.number(),
});

export const SearchAgentErrorSchema = SearchAgentBaseMessageSchema.extend({
    type: z.literal('error'),
});

export const SearchAgentQuestionSchema = SearchAgentBaseMessageSchema.extend({
    type: z.literal('user'),
});

export const SearchAgentAnswerSchema = SearchAgentBaseMessageSchema.extend({
    type: z.literal('assistant'),
    questionId: z.string().optional(),
});

export const IncomingAIMessageSchema = z.object({
    id: z.string(),
    message: z.string(),
});

export const SearchAgentResponseSchema = z.object({
    content: z.array(
        z.object({
            type: z.enum(['command', 'text']),
            body: z.string(),
        }),
    ),
});

export enum SearchAgentUpdateType {
    SEARCH_PRODUCTS = 'searchProducts',
    SEARCH_PRODUCTS_INTERNET = 'searchProductsInternet',
    SEARCH_COMPANIES = 'searchCompanies',
    SEARCH_DOCS = 'searchDocs',
    SEARCH_CASE_STUDIES = 'searchCaseStudies',
    ANALYZE_RESULTS = 'analyzeResults',
    EVALUATE_COMPATIBILITY = 'evaluateCompatibility',
    SUMMARIZE_REQUIREMENTS = 'summarizeRequirements',
    NEXT_STEPS = 'nextSteps',
}

export enum SearchAgentUpdateExtraInfo {
    SEARCH_WITHOUT_TRADESHOW_EVENT = 'Searching without tradeshow event',
    SEARCH_INTERNET = 'Searching internet',
}

const BaseSearchAgentUpdateSchema = {
    messageId: z.string().optional(),
};

export const SearchAgentUpdateSchema = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal(SearchAgentUpdateType.SEARCH_PRODUCTS),
            extraInfo: z.nativeEnum(SearchAgentUpdateExtraInfo).optional(),
            keywords: z.string().optional(),
            page: z.number().optional(),
            emphasis: z.string().optional(),
            requirements: z.string().optional(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.SEARCH_COMPANIES),
            keywords: z.string(),
            page: z.number().optional(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.SEARCH_DOCS),
            question: z.string(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.SEARCH_CASE_STUDIES),
            keywords: z.string(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.ANALYZE_RESULTS),
            keywords: z.string(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.EVALUATE_COMPATIBILITY),
            product: z.string(),
            specification: z.string(),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.SUMMARIZE_REQUIREMENTS),
        })
        .extend(BaseSearchAgentUpdateSchema),
    z
        .object({
            type: z.literal(SearchAgentUpdateType.NEXT_STEPS),
        })
        .extend(BaseSearchAgentUpdateSchema),
]);

export type SearchAgentMessage = z.infer<typeof SearchAgentMessageSchema>;
export type SearchAgentUserMessage = z.infer<typeof SearchAgentUserMessage>;
export type SearchAgentAssistantMessage = z.infer<typeof SearchAgentAssistantMessage>;

export type SearchAgentBaseMessage = z.infer<typeof SearchAgentBaseMessageSchema>;
export type SearchAgentError = z.infer<typeof SearchAgentErrorSchema>;
export type SearchAgentQuestion = z.infer<typeof SearchAgentQuestionSchema>;
export type SearchAgentAnswer = z.infer<typeof SearchAgentAnswerSchema>;
export type IncomingAIMessage = z.infer<typeof IncomingAIMessageSchema>;

export type SearchAgentResponse = z.infer<typeof SearchAgentResponseSchema>;
export type SearchAgentUpdate = z.infer<typeof SearchAgentUpdateSchema>;

export type SearchThread = {
    id: string;
    thread: {
        input: {
            id?: string;
            role: 'system' | 'user' | 'assistant' | 'developer' | 'update';
            content: string;
        }[];
    };
    createdAt: string;
    createdBy: string;
    email?: string | null;
    fallbackGoofy?: string | null;
    exhibitorMatch?: string | null;
    internal: boolean;
    event?: string | null;
    conversationDepth: number;
    aiAnalysis?: {
        summary: string;
        lastAnalyzedAt: string;
    } | null;
    swapcardUserId?: string | null;
};

export type ExhibitorMatch = {
    id: string;
    thread?: SearchThread | null;
    summarizeMatchResult?: string | null;
    requirements?: string | null;
    productSearchResults?: string[] | null;
    companySearchResults?: string[] | null;
    caseStudiesResults?: string[] | null;
    internetProductResults?: string | null;
    sentToCompanies?: {
        company: string;
        lead: string;
    }[];
    event?: string | null;
    correctlyAnswered: boolean;
    leadGenerated: boolean;
    leadShared: boolean;
    internalNotes: string;
    createdAt: string;
};

export type ExhibitorMatchLead = {
    id: string;
    exhibitorMatch: string;
    company: string;
    requirements: z.infer<typeof RTESchema>;
    email?: string | null;
    user?: string | null;
    event?: string | null;
    createdAt: string;
};
